import * as React from "react";
import { useRef, useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Popper,
  Typography,
  InputAdornment,
  ClickAwayListener,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Close } from "@mui/icons-material";
import {
  StyledMenuItemContainer,
  StyledMenuListItemText,
  HtmlTooltipDisabled,
} from "../StyledComponents/styled";
import { CustomTextInput } from "../TextInputs/style";

const MAX_LABEL_CHARS = 50;
const CHAR_WIDTH_PX = 6.5;
const DROPDOWN_PADDING_PX = 80;
const BOTTOM_MARGIN_PX = 100;
const TOP_MARGIN_PX = 8;
const MIN_DROPDOWN_HEIGHT = 150;
const FLIP_THRESHOLD_TO_TOP = 200;
const FLIP_THRESHOLD_TO_BOTTOM = 250;

const DROPDOWN_OPENED_EVENT = "dropdown-singleton-opened";

export default function SingleSelect({
  label,
  defaultVal,
  elemId,
  options,
  onChangeCall,
  fieldError,
  enableFullWidth,
  selectionCheck,
  sortOptions,
  readOnlyFlag,
  fieldInfo,
  allowClearance,
  usedFor,
  disableSearch,
  keepOpenOnScroll,
  disabledOptions,
  tooltipTitleForDisabledOptions,
  flip,
}) {
  const anchorRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const optionRefs = useRef({});
  const popperRef = useRef(null);

  const [selectedValue, setSelectedValue] = useState(defaultVal);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(MIN_DROPDOWN_HEIGHT);
  const [anchorIsFixed, setAnchorIsFixed] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Track placement both as state (for render) and as ref (for stable reads in callbacks)
  const placementRef = useRef("bottom-start");
  const [actualPlacement, setActualPlacement] = useState("bottom-start");

  const isDisabled = typeof readOnlyFlag !== "undefined" ? readOnlyFlag : false;
  const isFilter = usedFor === "filter";
  // Filter dropdowns always allow clearing; non-filter dropdowns fall back to the allowClearance prop
  const canClear = isFilter ? true : (typeof allowClearance !== "undefined" ? allowClearance : false);
  const applySorting = typeof sortOptions !== "undefined" ? sortOptions : true;
  const showSearch = typeof disableSearch !== "undefined" ? !disableSearch : true;
  const stayOpenOnScroll = true;
  const shrink =
    isFocused ||
    open ||
    (selectedValue !== null &&
      selectedValue !== "" &&
      Object.keys(selectedValue).length !== 0) ||
    isFilter;

  const IP_REGEX =
    /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(?:\/(\d{1,2}))?$/;

  const instanceId = useRef(
    elemId || `single-select-${Math.random().toString(36).substr(2, 9)}`
  );

  const updatePlacement = useCallback((p) => {
    placementRef.current = p;
    setActualPlacement(p);
  }, []);

  // Determine placement using hysteresis; the flip prop forces top-start when set
  const computePlacement = useCallback(() => {
    if (flip) return "top-start";
    if (!anchorRef.current) return placementRef.current;

    const { top, bottom } = anchorRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - bottom;
    const current = placementRef.current;
    const isTop = current.startsWith("top");

    if (!isTop && spaceBelow < FLIP_THRESHOLD_TO_TOP) return "top-start";
    if (isTop && spaceBelow > FLIP_THRESHOLD_TO_BOTTOM) return "bottom-start";
    return current;
  }, [flip]);

  // Compute max dropdown height based on resolved placement
  const computeHeight = useCallback((placement) => {
    if (!anchorRef.current) return MIN_DROPDOWN_HEIGHT;

    const { top, bottom } = anchorRef.current.getBoundingClientRect();
    const openingUpward = placement.startsWith("top");
    const available = openingUpward
      ? top - TOP_MARGIN_PX
      : window.innerHeight - bottom - BOTTOM_MARGIN_PX;

    return Math.max(available, MIN_DROPDOWN_HEIGHT);
  }, []);

  // Resolve placement first, then derive height from that placement
  const recalculate = useCallback(() => {
    const placement = computePlacement();
    updatePlacement(placement);
    setDropdownMaxHeight(computeHeight(placement));
  }, [computePlacement, computeHeight, updatePlacement]);

  const close = useCallback(
    (shouldRefocus = false) => {
      setOpen(false);
      setSearchTerm("");
      setHighlightedIndex(-1);
      if (shouldRefocus) anchorRef.current?.focus();
    },
    []
  );

  const detectAnchorIsFixed = useCallback(() => {
    let el = anchorRef.current?.parentElement;

    // Pre-scan the full ancestor chain for a Dialog before evaluating overflow;
    // DialogContent (overflow: auto) sits below the Dialog root in the DOM, so a
    // simple top-down traversal would return early at the overflow node and never
    // reach role="dialog"
    let insideDialog = false;
    let scanEl = el;
    while (scanEl && scanEl !== document.body) {
      if (scanEl.getAttribute("role") === "dialog") { insideDialog = true; break; }
      scanEl = scanEl.parentElement;
    }

    while (el && el !== document.body) {
      const style = window.getComputedStyle(el);
      const overflow = style.overflow + style.overflowY + style.overflowX;

      if (/auto|scroll|overlay/.test(overflow)) {
        // Anchor scrolls inside this container; fixed strategy is required when
        // that container belongs to a Dialog so the Popper can track the anchor
        return insideDialog;
      }

      if (style.position === "sticky" || style.position === "fixed") return true;

      el = el.parentElement;
    }

    return insideDialog;
  }, []);

  useEffect(() => {
    const handleOtherDropdownOpened = (e) => {
      if (e.detail?.id !== instanceId.current && open) close();
    };

    window.addEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    return () => {
      window.removeEventListener(DROPDOWN_OPENED_EVENT, handleOtherDropdownOpened);
    };
  }, [open, close]);

  useEffect(() => {
    if (open) {
      const event = new CustomEvent(DROPDOWN_OPENED_EVENT, {
        detail: { id: instanceId.current },
      });
      window.dispatchEvent(event);
    }
  }, [open]);

  // Close when anchor exits the viewport in the direction the dropdown is open;
  // for fixed-strategy anchors also force a synchronous position update on scroll
  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      if (anchorRef.current) {
        const { top, bottom } = anchorRef.current.getBoundingClientRect();
        const openingUpward = placementRef.current.startsWith("top");
        if (openingUpward && top > window.innerHeight) { close(false); return; }
        if (!openingUpward && bottom < 0) { close(false); return; }
      }
      // Absolute-strategy poppers follow document scroll natively; only fixed ones need JS nudging
      if (anchorIsFixed) popperRef.current?.forceUpdate();
    };

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", handleScroll, { capture: true });
  }, [open, anchorIsFixed, close]);

  // On open: resolve placement first, then compute height; focus search input or
  // the Paper itself so keyboard navigation works even without a search field
  useEffect(() => {
    if (!open) return;

    setAnchorIsFixed(detectAnchorIsFixed());
    recalculate();

    if (showSearch) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else {
      setTimeout(() => dropdownRef.current?.focus(), 0);
    }
  }, [open, detectAnchorIsFixed, recalculate, showSearch]);

  // On viewport resize or zoom: recalculate placement and height while open
  useEffect(() => {
    if (!open) return;

    const handleResize = () => recalculate();
    const vp = window.visualViewport;

    if (vp) {
      vp.addEventListener("resize", handleResize);
      return () => vp.removeEventListener("resize", handleResize);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [open, recalculate]);

  useEffect(() => {
    setSelectedValue(defaultVal);
  }, [defaultVal]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const handleSelect = useCallback((option) => {
    if (isDisabled) return;

    setSelectedValue(option);
    onChangeCall(option);
    setHighlightedIndex(-1);
    close();
  }, [isDisabled, onChangeCall, close]);

  const handleClear = (e) => {
    e.stopPropagation();
    if (isDisabled) return;

    setSelectedValue(null);
    onChangeCall(null);
  };

  const compareIp = (a, b) => {
    const matchA = a.match(IP_REGEX);
    const matchB = b.match(IP_REGEX);

    if (!matchA || !matchB) return null;

    for (let i = 1; i <= 4; i++) {
      const diff = parseInt(matchA[i], 10) - parseInt(matchB[i], 10);
      if (diff !== 0) return diff;
    }

    const prefixA = matchA[5] !== undefined ? parseInt(matchA[5], 10) : 32;
    const prefixB = matchB[5] !== undefined ? parseInt(matchB[5], 10) : 32;
    return prefixA - prefixB;
  };

  const sortedOptions = useMemo(() => {
    if (!applySorting) return [...options];

    return [...options].sort((a, b) => {
      const labelA = String(a.label).trim();
      const labelB = String(b.label).trim();

      const ipResult = compareIp(labelA, labelB);
      if (ipResult !== null) return ipResult;

      const isANumeric = !isNaN(labelA);
      const isBNumeric = !isNaN(labelB);

      if (isANumeric && isBNumeric) return Number(labelA) - Number(labelB);
      if (!isANumeric && !isBNumeric) return labelA.localeCompare(labelB);
      return isANumeric ? -1 : 1;
    });
  }, [options, applySorting]);

  const filteredOptions = useMemo(() => {
    if (!showSearch) return sortedOptions;

    const searchValue = searchTerm.trim().toLowerCase();
    if (!searchValue) return sortedOptions;

    return sortedOptions.filter((option) => {
      const optionLabel = String(option?.label ?? "").toLowerCase();
      return optionLabel.includes(searchValue);
    });
  }, [sortedOptions, searchTerm, showSearch]);

  const isOptionDisabledFn = useCallback(
    (option) => typeof disabledOptions === "function" && disabledOptions(option),
    [disabledOptions]
  );

  const scrollHighlightedIntoView = useCallback((index) => {
    const element = optionRefs.current[`option-${index}`];
    if (element) element.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, []);

  const handleListKeyDown = useCallback(
    (e) => {
      if (!open) return;

      const totalCount = filteredOptions.length;
      if (totalCount === 0 && e.key !== "Escape") return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev < totalCount - 1 ? prev + 1 : 0;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setHighlightedIndex((prev) => {
            const next = prev > 0 ? prev - 1 : totalCount - 1;
            setTimeout(() => scrollHighlightedIntoView(next), 0);
            return next;
          });
          break;

        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
            const option = filteredOptions[highlightedIndex];
            if (!isOptionDisabledFn(option)) handleSelect(option);
          }
          break;

        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          close();
          break;

        default:
          break;
      }
    },
    [open, filteredOptions, highlightedIndex, close, isOptionDisabledFn, scrollHighlightedIntoView, handleSelect]
  );

  const dropdownWidth = useMemo(() => {
    const anchorWidth = anchorRef.current?.offsetWidth ?? 0;

    let maxLabelLength = 0;
    sortedOptions.forEach((opt) => {
      const labelLength = String(opt?.label ?? "").length;
      if (labelLength > maxLabelLength) maxLabelLength = labelLength;
    });

    const clampedLength = Math.min(maxLabelLength, MAX_LABEL_CHARS);
    const contentWidth = clampedLength * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX;

    return Math.max(anchorWidth, contentWidth);
  }, [sortedOptions, open]);

  const maxWidthStyle = enableFullWidth
    ? { maxWidth: "100%" }
    : { maxWidth: "220px", width: "100%" };

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (open) close(false);
      }}
    >
      <Box sx={{ width: "100%", position: "relative", ...maxWidthStyle }}>
        <Box
          id={elemId}
          ref={anchorRef}
          role="combobox"
          aria-expanded={open}
          aria-haspopup="listbox"
          tabIndex={isDisabled ? -1 : 0}
          onClick={() => {
            if (!isDisabled) setOpen((prev) => !prev);
          }}
          onFocus={() => {
            if (!isDisabled) setIsFocused(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (isDisabled) return;
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
            if (e.key === "Escape") close();
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
              e.preventDefault();
              if (!open) setOpen(true);
            }
          }}
          sx={{
            position: "relative",
            cursor: isDisabled ? "default" : "pointer",
            outline: "none",
            pt: 2.1,
            borderBottom: "1px solid #AF3241",
            opacity: isDisabled ? 0.6 : 1,
            "& .cap-clear-btn": { opacity: 0 },
            "&:hover .cap-clear-btn": { opacity: 1 },
            "& .cap-clear-btn-visible": { opacity: 1 },
            "&::after": {
              content: '""',
              position: "absolute",
              left: 0,
              bottom: -1,
              width: open ? "100%" : "0%",
              height: "2px",
              backgroundColor: "#AF3241",
              transition: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: shrink ? 0 : 22,
              fontSize: shrink ? 12 : 14,
              color: "#AF3241",
              pointerEvents: "none",
              transformOrigin: "top left",
              transition: "all 160ms ease",
            }}
          >
            {label}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                py: 0.5,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isDisabled ? "#62626E" : "#AF3241",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {selectedValue
                  ? selectedValue.label
                  : isFilter
                  ? "All Results"
                  : ""}
              </Typography>
            </Box>

            <Box
              sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 0.4 }}
            >
              {canClear && selectedValue && !isDisabled && (
                <Close
                  className={`cap-clear-btn${open || isFocused ? " cap-clear-btn-visible" : ""}`}
                  onClick={handleClear}
                  sx={{ cursor: "pointer", fontSize: 18 }}
                />
              )}
              {typeof fieldInfo !== "undefined" && fieldInfo}
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </Box>
          </Box>
        </Box>

        {fieldError && fieldError !== "" && (
          <Typography
            variant="caption"
            sx={{ color: "#AF3241", mt: 0.5, display: "block" }}
          >
            {fieldError}
          </Typography>
        )}

        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={actualPlacement}
          popperRef={popperRef}
          disablePortal={false}
          popperOptions={{ strategy: anchorIsFixed ? "fixed" : "absolute" }}
          sx={{ zIndex: 1400 }}
          modifiers={[
            {
              name: "eventListeners",
              options: { scroll: false, resize: false },
            },
            {
              // Placement and height are managed by recalculate(); the dropdown
              // scrolls naturally with its anchor rather than being pinned to the viewport
              name: "preventOverflow",
              enabled: false,
            },
            {
              // Flip is managed by recalculate(); disable Popper's built-in modifier
              name: "flip",
              enabled: false,
            },
          ]}
          style={{
            minWidth: anchorRef.current?.offsetWidth ?? undefined,
            width: Math.min(
              dropdownWidth,
              anchorRef.current?.offsetWidth ?? dropdownWidth
            ),
            maxWidth:
              anchorRef.current?.offsetWidth ??
              MAX_LABEL_CHARS * CHAR_WIDTH_PX + DROPDOWN_PADDING_PX,
          }}
        >
          <Paper
            ref={dropdownRef}
            role="listbox"
            tabIndex={-1}
            sx={{
              maxHeight: dropdownMaxHeight,
              overflowY: "auto",
              overflowX: "hidden",
              outline: "none",
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onKeyDown={handleListKeyDown}
          >
            {showSearch && (
              <Box sx={{ p: 1 }} onClick={(e) => e.stopPropagation()}>
                <CustomTextInput
                  inputRef={searchInputRef}
                  id={elemId + "_search"}
                  size="small"
                  fullWidth
                  autoComplete="off"
                  spellCheck={false}
                  label="Search"
                  value={searchTerm}
                  variant="standard"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleListKeyDown}
                  InputProps={{
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <Close
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSearchTerm("");
                          }}
                          sx={{ cursor: "pointer", color: "#AF3241" }}
                        />
                      </InputAdornment>
                    ) : undefined,
                  }}
                />
              </Box>
            )}

            {filteredOptions.map((option, index) => {
              const optionKey = option?.label ?? index;
              const isSelectedOption =
                selectedValue && selectionCheck(option, selectedValue);
              const isOptionDisabled = isOptionDisabledFn(option);
              const isHighlighted = highlightedIndex === index;

              const menuItem = (
                <StyledMenuItemContainer
                  key={isOptionDisabled ? undefined : optionKey}
                  ref={(el) => {
                    optionRefs.current[`option-${index}`] = el;
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isOptionDisabled) handleSelect(option);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginRight: 0,
                    backgroundColor: isHighlighted
                      ? "rgba(175, 50, 65, 0.08)"
                      : isSelectedOption
                      ? "rgba(175, 50, 65, 0.04)"
                      : "transparent",
                    opacity: isOptionDisabled ? 0.5 : 1,
                    cursor: isOptionDisabled ? "default" : "pointer",
                  }}
                >
                  <Typography
                    variant="body2"
                    className="selectorOptions"
                    sx={{
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      color: isSelectedOption ? "#AF3241" : "inherit",
                      fontWeight: isSelectedOption ? 500 : 400,
                    }}
                  >
                    {option.label}
                  </Typography>
                </StyledMenuItemContainer>
              );

              if (isOptionDisabled) {
                return (
                  <HtmlTooltipDisabled
                    key={optionKey}
                    title={tooltipTitleForDisabledOptions ?? "This option is unavailable"}
                    arrow
                  >
                    <span>{menuItem}</span>
                  </HtmlTooltipDisabled>
                );
              }

              return menuItem;
            })}

            {filteredOptions.length === 0 && (
              <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="textSecondary">
                  No options found
                </Typography>
              </Box>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
